/**
 * UserApiService
 * Handles all user-related API endpoints
 */

import type { HttpClient } from './HttpClient';
import type {
  CommonModelsUserModelsUser,
  UserCreate,
  UserUpdate,
} from '../../api/data-contracts';

/**
 * User pagination parameters
 */
export interface UserPaginationParams {
  skip?: number;
  limit?: number;
}

/**
 * Service for user operations
 */
export class UserApiService {
  constructor(private readonly httpClient: HttpClient) {}

  /**
   * Get paginated list of users (admin only)
   */
  public async getUsers(params: UserPaginationParams = {}): Promise<CommonModelsUserModelsUser[]> {
    const { skip = 0, limit = 20 } = params;

    return this.httpClient.get<CommonModelsUserModelsUser[]>('/users', {
      params: { skip, limit },
    });
  }

  /**
   * Get user by username (admin only)
   */
  public async getUserByUsername(username: string): Promise<CommonModelsUserModelsUser> {
    return this.httpClient.get<CommonModelsUserModelsUser>(`/users/${username}`);
  }

  /**
   * Create a new user (admin only)
   */
  public async createUser(userData: UserCreate): Promise<CommonModelsUserModelsUser> {
    // Normalize the user data to match API expected format
    const userCreateData: UserCreate = {
      username: userData.username,
      email: userData.email,
      password: userData.password,
      full_name: userData.full_name || null,
      organization_id: userData.organization_id || null,
      role: userData.role,
    };

    return this.httpClient.post<CommonModelsUserModelsUser>('/users', userCreateData);
  }

  /**
   * Update a user (admin only)
   */
  public async updateUser(
    username: string,
    updates: Partial<UserUpdate>
  ): Promise<CommonModelsUserModelsUser> {
    // Normalize the update data
    const userUpdateData: Partial<UserUpdate> = {};

    if (updates.email !== undefined) {
      userUpdateData.email = updates.email;
    }
    if (updates.full_name !== undefined) {
      userUpdateData.full_name = updates.full_name;
    }
    if (updates.company !== undefined) {
      userUpdateData.company = updates.company;
    }
    if (updates.role !== undefined) {
      userUpdateData.role = updates.role;
    }
    if (updates.active !== undefined) {
      userUpdateData.active = updates.active;
    }
    if (updates.password !== undefined && updates.password) {
      userUpdateData.password = updates.password;
    }

    return this.httpClient.put<CommonModelsUserModelsUser>(`/users/${username}`, userUpdateData);
  }

  /**
   * Delete a user (admin only)
   */
  public async deleteUser(username: string): Promise<{ message: string }> {
    return this.httpClient.delete<{ message: string }>(`/users/${username}`);
  }
}
